import { createContext, memo, type ReactNode, useCallback, useContext, useMemo, useRef } from 'react';

export type Subscriber<T> = (callback: (element: T) => void) => { unsubscribe: () => void };

type ElementWithPageNumber = {
  element: HTMLElement | null;
  pageNumber: number;
};

export type PDFCurrentSearchResultElementContextType = {
  getCurrentSearchResultElement: () => ElementWithPageNumber | null;
  clearElementForPage: (pageNumber: number) => void;
  setCurrentSearchResultElement: (element: ElementWithPageNumber | null) => void;
  subscribeToCurrentSearchResultElement: Subscriber<ElementWithPageNumber | null>;
};

const defaultValue: PDFCurrentSearchResultElementContextType = {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  clearElementForPage: () => {},
  getCurrentSearchResultElement: () => null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setCurrentSearchResultElement: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  subscribeToCurrentSearchResultElement: () => ({ unsubscribe: () => {} }),
};

export const PDFCurrentSearchResultElementContext =
  createContext<PDFCurrentSearchResultElementContextType>(defaultValue);

type PDFCurrentSearchResultElementContextProviderProps = {
  children: ReactNode;
};

export const PDFCurrentSearchResultElementContextProvider = memo(
  ({ children }: PDFCurrentSearchResultElementContextProviderProps) => {
    const currentSearchResultElementRef = useRef<ElementWithPageNumber | null>(null);
    const subscribersRef = useRef<Set<(element: ElementWithPageNumber | null) => void>>(new Set());

    const getCurrentSearchResultElement = useCallback(() => currentSearchResultElementRef.current, []);

    const setCurrentSearchResultElement = useCallback((element: ElementWithPageNumber | null) => {
      if (element !== currentSearchResultElementRef.current) {
        subscribersRef.current.forEach((subscriber) => {
          subscriber(element);
        });
        currentSearchResultElementRef.current = element;
      }
    }, []);

    const subscribeToCurrentSearchResultElement = useCallback<Subscriber<ElementWithPageNumber | null>>((callback) => {
      const subscriber = (element: ElementWithPageNumber | null) => {
        callback(element);
      };
      subscribersRef.current.add(subscriber);
      return {
        unsubscribe: () => {
          subscribersRef.current.delete(subscriber);
        },
      };
    }, []);

    const clearElementForPage = useCallback(
      (pageNumber: number) => {
        if (currentSearchResultElementRef.current?.pageNumber === pageNumber) {
          setCurrentSearchResultElement(null);
        }
      },
      [setCurrentSearchResultElement],
    );

    const value = useMemo<PDFCurrentSearchResultElementContextType>(
      () => ({
        clearElementForPage,
        getCurrentSearchResultElement,
        setCurrentSearchResultElement,
        subscribeToCurrentSearchResultElement,
      }),
      [
        clearElementForPage,
        getCurrentSearchResultElement,
        setCurrentSearchResultElement,
        subscribeToCurrentSearchResultElement,
      ],
    );

    return (
      <PDFCurrentSearchResultElementContext.Provider value={value}>
        {children}
      </PDFCurrentSearchResultElementContext.Provider>
    );
  },
);

PDFCurrentSearchResultElementContextProvider.displayName = 'PDFCurrentSearchResultElementContextProvider';

export const usePDFCurrentSearchResultElementContext = () => {
  const context = useContext(PDFCurrentSearchResultElementContext);

  if (context === defaultValue) {
    throw new Error(
      'usePDFCurrentSearchResultElementContext must be used within a PDFCurrentSearchResultElementContextProvider',
    );
  }

  return context;
};
